import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import type { KeyboardEvent } from 'react';
import { sumarTecla, terminaEnLectura, type Tecla } from '../../lib/lector';

export interface EntradaEscaneoHandle {
  enfocar: () => void;
  /** Pone en la entrada lo que ya leyó el lector en otro campo; el resto de la lectura llega aquí. */
  recibir: (prefijo: string) => void;
}

function esEditable(el: Element | null): boolean {
  return el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement;
}

/**
 * Input de escaneo con foco permanente: el lector keyboard-wedge teclea el código y Enter (docs/02-ux.md
 * §5). Si pierde el foco, se re-enfoca solo salvo que quede en otro campo editable.
 */
export const EntradaEscaneo = forwardRef<EntradaEscaneoHandle, { onCodigo: (codigo: string) => void; pausado: boolean }>(
  function EntradaEscaneo({ onCodigo, pausado }, ref) {
    const input = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => ({
      enfocar: () => input.current?.focus(),
      recibir: (prefijo) => {
        const campo = input.current;
        if (!campo) return;
        campo.value = prefijo;
        campo.focus();
        campo.setSelectionRange(prefijo.length, prefijo.length);
      },
    }));

    // Con el foco en un botón, lo que se teclea va a la entrada: así el Enter del lector no presiona el
    // botón. Con un diálogo abierto no se mueve nada, y una lectura que termina en Enter no lo activa.
    useEffect(() => {
      let rafaga: Tecla[] = [];
      function alTeclearEnVentana(e: globalThis.KeyboardEvent) {
        if (e.defaultPrevented || e.isComposing || e.ctrlKey || e.metaKey || e.altKey) return;
        if (esEditable(document.activeElement)) {
          rafaga = [];
          return;
        }
        const hayDialogo = document.querySelector('[aria-modal="true"]') !== null;
        if (e.key === 'Enter') {
          if (hayDialogo && terminaEnLectura(rafaga, e.timeStamp)) {
            e.preventDefault();
            e.stopPropagation();
          }
          rafaga = [];
          return;
        }
        if (e.repeat || e.key.length !== 1 || e.key === ' ') return;
        if (hayDialogo) {
          rafaga = sumarTecla(rafaga, { tecla: e.key, t: e.timeStamp });
          return;
        }
        const campo = input.current;
        if (!campo) return;
        e.preventDefault();
        campo.focus();
        campo.value += e.key;
      }
      window.addEventListener('keydown', alTeclearEnVentana, true);
      return () => window.removeEventListener('keydown', alTeclearEnVentana, true);
    }, []);

    function alTeclear(e: KeyboardEvent<HTMLInputElement>) {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      const valor = input.current?.value ?? '';
      if (input.current) input.current.value = '';
      if (valor.trim() !== '') onCodigo(valor);
    }

    function alPerderFoco() {
      if (pausado) return;
      setTimeout(() => {
        if (!esEditable(document.activeElement) && !pausado) input.current?.focus();
      }, 120);
    }

    return (
      <input
        ref={input}
        autoFocus
        onKeyDown={alTeclear}
        onBlur={alPerderFoco}
        placeholder="Escanea aquí o digita el código y Enter"
        aria-label="Código de producto"
        data-e2e="entrada-escaneo"
        className="num rounded-control border-primary/60 w-full border-2 bg-card px-4 py-3 text-escaneo outline-none focus:ring-2 focus:ring-primary/40"
      />
    );
  }
);
